import { useState } from "react";
import { BellRing } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useApi } from "../hooks/useApi";
import { useAuth } from "../context/AuthContext";
import { ukDate, ukDateTime } from "../lib/format";
import { Loading, Panel } from "./common";
import { Button } from "./ui/button";
import { DateField } from "./DateField";

function Enforcement({ enf, reload }) {
  const { user } = useAuth();
  const [cutoff, setCutoff] = useState(enf.cutoff);
  const admin = user.roles.includes("Admin");
  const save = async (mode) => { try { await api.put("/admin/payment-log-settings", { mode, cutoff }); toast.success("Saved"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Panel eyebrow="Admin setting" title="Payment log template enforcement" testid="pl-enforcement">
      <div className="flex flex-wrap items-end gap-3 text-sm">
        {["transition", "strict"].map((m) => <Button key={m} variant={enf.mode === m ? "default" : "outline"} disabled={!admin} onClick={() => save(m)} data-testid={`pl-mode-${m}`}>{m === "transition" ? "Transition – old layouts accepted with a reminder" : "Strict – EPIL template only"}</Button>)}
        <label className="flex items-center gap-2 text-xs">Cut-off date<DateField className="w-40" value={cutoff} disabled={!admin} onChange={setCutoff} testid="pl-cutoff" /></label>
        {admin && <Button size="sm" variant="ghost" onClick={() => save(enf.mode)} data-testid="pl-cutoff-save">Save date</Button>}
      </div>
      <p className="mt-2 text-xs text-muted-foreground" data-testid="pl-mode-current">Now: <b>{enf.mode === "transition" ? `Transition until ${ukDate(enf.cutoff)}` : "Strict"}</b></p>
    </Panel>
  );
}

export function TemplateAdoption() {
  const { data, reload } = useApi("/admin/template-adoption");
  const [busy, setBusy] = useState(null);
  if (!data) return <Loading />;
  const remind = async (r) => {
    setBusy(r.partner_id);
    try { const { data: d } = await api.post(`/admin/template-adoption/${r.partner_id}/remind`); toast.success(d.users ? `In-app reminder sent to ${d.users} user(s); email recorded in the outbox (sending is off)` : "Partner has no portal users – email to the partner contact recorded in the outbox (sending is off)"); reload(true); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  return (
    <div className="space-y-6" data-testid="template-adoption">
      <Enforcement enf={data.enforcement} reload={() => reload(true)} />
      <Panel eyebrow="Per partner" title="Template adoption" testid="adoption-panel">
        <div className="overflow-x-auto"><table className="dt w-full text-sm"><thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-2">Partner</th><th>Last upload</th><th>Layout</th><th>Old uploads</th><th>Days to cut-off</th><th /></tr></thead>
          <tbody>{data.rows.map((r) => <tr key={r.partner_id} className="border-b" data-testid={`adoption-row-${r.partner_id}`}>
            <td className="py-2 font-semibold">{r.partner_name}</td><td className="text-xs">{r.last_upload ? ukDateTime(r.last_upload) : "—"}</td>
            <td>{r.layout ? <span className={`rounded px-1.5 py-0.5 text-[11px] font-bold ${r.layout === "Old" ? "bg-[hsl(var(--warning)/0.25)]" : "bg-[hsl(var(--positive)/0.15)] text-[hsl(var(--positive))]"}`} data-testid={`adoption-layout-${r.partner_id}`}>{r.layout === "Old" ? "Old layout" : "Standard"}</span> : "—"}</td>
            <td className="font-figure">{r.old_uploads}</td><td className="font-figure" data-testid={`adoption-days-${r.partner_id}`}>{data.enforcement.mode === "strict" ? "Strict" : r.days_until_cutoff}</td>
            <td className="text-right">{r.layout !== "Standard" && <Button size="sm" variant="outline" disabled={busy === r.partner_id} onClick={() => remind(r)} data-testid={`adoption-remind-${r.partner_id}`}><BellRing className="mr-1 h-3.5 w-3.5" />Send reminder</Button>}
              {r.last_reminder && <div className="text-[10px] text-muted-foreground">reminded {ukDate(r.last_reminder)}</div>}</td></tr>)}</tbody></table></div>
      </Panel>
    </div>
  );
}
